import { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { championshipApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { 
  Medal, 
  Loader2,
  Trophy,
  Award,
  Calculator,
  Users,
  ShoppingCart,
  AlertTriangle,
  Info,
  Save,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import MedalDeliveryWidget from '../components/MedalDeliveryWidget';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { EstadoVazio } from '../components/common/Estados';

function SummaryCard({ title, subtitle, icon, data, highlight, showSecondBronze }) {
  return (
    <div
      className={`rounded-xl border p-5 ${
        highlight ? 'border-[#D4AF37]/40 bg-[#D4AF37]/5' : 'border-[#27272A] bg-[#1A1A1A]'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h4 className="font-semibold text-white font-['Chivo']">{title}</h4>
      </div>
      <p className="text-xs text-[#71717A] mb-4 min-h-[2rem]">{subtitle}</p>
      {!data ? (
        <div className="flex items-center gap-2 text-sm text-[#71717A]">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculando...
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-[#A1A1AA]">
              <Medal className="w-4 h-4 medalha-ouro" /> Ouro
            </span>
            <span className="font-bold text-white">{data.gold ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-[#A1A1AA]">
              <Medal className="w-4 h-4 medalha-prata" /> Prata
            </span>
            <span className="font-bold text-white">{data.silver ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-[#A1A1AA]">
              <Medal className="w-4 h-4 medalha-bronze" /> Bronze
            </span>
            <span className="font-bold text-white">{data.bronze ?? 0}</span>
          </div>
          {showSecondBronze && (
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                <Medal className="w-4 h-4 medalha-segundo-bronze" /> 2º Bronze
              </span>
              <span className="font-bold text-white">{data.second_bronze ?? 0}</span>
            </div>
          )}
          <div className="pt-2 border-t border-[#27272A] flex items-center justify-between">
            <span className="text-sm text-[#71717A]">Total</span>
            <span className={`font-bold ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}>
              {data.total ?? 0}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MedalsPage() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const contextId = params.id || searchParams.get('championship_id') || '';
  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(contextId);
  const [medalsData, setMedalsData] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [savingEstimate, setSavingEstimate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChampionships = async () => {
      try {
        const { data } = await championshipApi.list();
        setChampionships(data);
      } catch (error) {
        toast.error('Erro ao carregar campeonatos');
      } finally {
        setLoading(false);
      }
    };
    fetchChampionships();
  }, []);

  useEffect(() => {
    if (selectedChampionship) {
      fetchMedals();
      fetchEstimate();
    }
  }, [selectedChampionship]);

  const fetchMedals = async () => {
    try {
      const { data } = await championshipApi.getMedals(selectedChampionship);
      setMedalsData(data);
    } catch (error) {
      toast.error('Erro ao carregar medalhas');
    }
  };

  const fetchEstimate = async () => {
    try {
      const { data } = await championshipApi.getMedalEstimate(selectedChampionship);
      setEstimate(data);
      setManualInput(data.estimated_athletes != null ? String(data.estimated_athletes) : '');
    } catch (error) {
      toast.error('Erro ao carregar a estimativa de medalhas');
    }
  };

  const handleSaveEstimate = async () => {
    let value = null;
    const trimmed = manualInput.trim();
    if (trimmed !== '') {
      const parsed = Number(trimmed);
      if (!Number.isInteger(parsed) || parsed < 0) {
        toast.error('Informe um número inteiro maior ou igual a zero');
        return;
      }
      value = parsed;
    }
    setSavingEstimate(true);
    try {
      await championshipApi.saveMedalEstimate(selectedChampionship, value);
      toast.success(value === null ? 'Estimativa removida' : 'Estimativa salva');
      await fetchEstimate();
    } catch (error) {
      toast.error('Erro ao salvar a estimativa');
    } finally {
      setSavingEstimate(false);
    }
  };

  const renderGroups = (groups) => (
    <div className="overflow-x-auto">
      <table className="tabela w-full">
        <thead>
          <tr className="border-b border-[#27272A]">
            <th className="text-left py-2 px-3 text-[#71717A]">Categoria (pré-chave)</th>
            <th className="text-center py-2 px-3 text-[#71717A]">Atletas</th>
            <th className="text-center py-2 px-3 text-[#71717A]">Ouro</th>
            <th className="text-center py-2 px-3 text-[#71717A]">Prata</th>
            <th className="text-center py-2 px-3 text-[#71717A]">Bronze</th>
            {estimate?.has_second_bronze && (
              <th className="text-center py-2 px-3 text-[#71717A]">2º Bronze</th>
            )}
          </tr>
        </thead>
        <tbody>
          {groups.length === 0 ? (
            <tr>
              <td colSpan={estimate?.has_second_bronze ? 6 : 5} className="py-6 text-center text-[#71717A]">
                Nenhuma inscrição nesta modalidade
              </td>
            </tr>
          ) : (
            groups.map((g) => (
              <tr key={g.key} className="border-b border-[#27272A]/50">
                <td className="py-2 px-3 text-white font-medium">
                  {g.display}
                  {g.over_16 && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-micro text-amber-400"
                      title="O sistema hoje gera chave com no máximo 16 atletas"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" /> 17+ atletas
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-center text-[#A1A1AA]">{g.athletes}</td>
                <td className="py-2 px-3 text-center medalha-ouro font-bold">{g.gold}</td>
                <td className="py-2 px-3 text-center medalha-prata font-bold">{g.silver}</td>
                <td className="py-2 px-3 text-center medalha-bronze font-bold">{g.bronze}</td>
                {estimate?.has_second_bronze && (
                  <td className="py-2 px-3 text-center medalha-segundo-bronze font-bold">{g.second_bronze ?? 0}</td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <PageContent>
      <div className="space-y-6" data-testid="medals-page">
        <CabecalhoPagina
          contexto="Competição"
          titulo="Quadro de Medalhas"
          descricao="Visualize as medalhas por campeonato"
          icone={Medal}
        />

        {/* Filtro em faixa própria (Etapa Visual 2) */}
        <FaixaFiltros rotulo="Campeonato">
        <div className="max-w-xs">
          <Label className="text-[#A1A1AA]">Campeonato</Label>
          <Select value={selectedChampionship} onValueChange={setSelectedChampionship}>
            <SelectTrigger data-testid="select-championship" className="bg-[#141414] border-[#27272A] text-white mt-2">
              <SelectValue placeholder="Selecione um campeonato" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              {championships.map((champ) => (
                <SelectItem key={champ._id} value={champ._id} className="text-white hover:bg-[#27272A]">
                  {champ.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        </FaixaFiltros>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
          </div>
        ) : !selectedChampionship ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={Medal} titulo="Selecione um campeonato" descricao="Escolha um campeonato para ver o quadro de medalhas" />
            </CardContent>
          </Card>
        ) : (
          <>
            <Tabs defaultValue="estimativa" className="w-full">
              <TabsList className="bg-[#1A1A1A] border border-[#27272A] p-1 mb-4">
                <TabsTrigger value="estimativa" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA]">
                  <Calculator className="w-4 h-4 mr-2" />Estimativa
                </TabsTrigger>
                <TabsTrigger value="entrega" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA]">
                  <CheckCircle className="w-4 h-4 mr-2" />Entrega
                </TabsTrigger>
              </TabsList>

              <TabsContent value="estimativa">
            {/* Medal Estimate */}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-[#D4AF37]" />
                  Estimativa de Medalhas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* 2nd bronze notice */}
                {estimate?.has_second_bronze && (
                  <div className="flex items-center gap-2 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3 text-sm text-[#A1A1AA]">
                    <Award className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                    Este campeonato terá o 2º Bronze: 1º Bronze com pontos + 2º Bronze com 0 pontos (estilo CBJJ, sem disputa pelo 3º lugar).
                  </div>
                )}

                {/* Manual estimate input */}
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1 max-w-xs">
                    <Label className="text-[#A1A1AA]">Estimativa de Atletas (projeção para compra)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={manualInput}
                      onChange={(e) => setManualInput(e.target.value)}
                      placeholder="Ex.: 724"
                      data-testid="medal-estimate-input"
                      className="bg-[#141414] border-[#27272A] text-white mt-2"
                    />
                    <p className="text-xs text-[#71717A] mt-1">
                      Preencha com a projeção total de atletas para antecipar a compra das medalhas.
                    </p>
                  </div>
                  <Button
                    onClick={handleSaveEstimate}
                    disabled={savingEstimate}
                    data-testid="medal-estimate-save"
                    className="bg-[#D4AF37] text-black hover:bg-[#E6C65C]"
                  >
                    {savingEstimate ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Salvar estimativa
                  </Button>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SummaryCard
                    title="Projeção Manual"
                    subtitle="Baseada na estimativa informada (25% ouro / 25% prata / 50% bronze)"
                    icon={<Users className="w-5 h-5 text-[#A1A1AA]" />}
                    data={estimate ? estimate.manual : null}
                  />
                  <SummaryCard
                    title="Calculado por Inscrições"
                    subtitle={`Exato por pré-chave — ${estimate?.registered_total ?? 0} inscritos (${estimate?.registered_confirmed ?? 0} confirmados + ${estimate?.registered_pending ?? 0} pendentes)`}
                    icon={<Calculator className="w-5 h-5 text-[#A1A1AA]" />}
                    data={estimate ? estimate.calculated : null}
                    showSecondBronze={estimate?.has_second_bronze}
                  />
                  <SummaryCard
                    title="Recomendado para Compra"
                    subtitle="Máximo entre a projeção manual e o calculado, por cor"
                    icon={<ShoppingCart className="w-5 h-5 text-[#D4AF37]" />}
                    data={estimate ? estimate.recommended : null}
                    highlight
                    showSecondBronze={estimate?.has_second_bronze}
                  />
                </div>

                {/* Breakdown per pre-bracket */}
                {estimate && (
                  <>
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-2">Pré-chaves por Categoria (Peso)</h4>
                      {renderGroups(estimate.weight_groups || [])}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-2">Pré-chaves Absoluto</h4>
                      {renderGroups(estimate.absolute_groups || [])}
                    </div>
                    <div className="flex items-start gap-2 text-xs text-[#71717A]">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>
                        O cálculo por inscrições usa a mesma chave de agrupamento das chaves
                        (idade | faixa | sexo | peso) e conta TODAS as inscrições, mesmo sem pagamento
                        confirmado. Regra por chave: 1 atleta → 1 ouro · 2 → 1 ouro + 1 prata · 3 → + 1 bronze ·
                        4 ou mais → 1 ouro + 1 prata + {estimate?.has_second_bronze ? '1 bronze + 1 2º bronze (2º vale 0 pontos, estilo CBJJ)' : '2 bronzes'}. O Absoluto gera medalhas extras. Categorias
                        com mais de 16 atletas hoje geram apenas 1 chave com 16 — o cálculo espelha esse comportamento.
                      </p>
                      {estimate?.has_second_bronze && (
                        <p className="mt-2">
                          O 4º lugar (2º Bronze) existe em toda chave de 4 ou mais atletas: o sistema
                          considera SEMIFINAL toda luta cujo vencedor vai à final (por isso as chaves de
                          5, 6, 10, 11 e 12 atletas premiam 4). <strong>Exceção:</strong> quando um dos
                          finalistas chega à final apenas por passes livres (ex.: chave de 9 atletas),
                          existe uma só semifinal real e saem <strong>3</strong> colocações — e esta
                          estimativa <strong>já considera essa exceção</strong> (a chave de 9 é contada
                          com 3 medalhas). A contagem definitiva continua sendo a da{' '}
                          <strong>Entrega de Medalhas</strong>, que lê a colocação real de cada chave.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
              </TabsContent>

              <TabsContent value="entrega">
            {/* ============ ENTREGA DE MEDALHAS ============ */}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37]" />
                  Entrega de Medalhas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#71717A] mb-4">
                  Registre a situação da entrega de cada medalha aos atletas premiados.
                </p>
                <MedalDeliveryWidget
                  championshipId={selectedChampionship}
                  compact={false}
                />
              </CardContent>
            </Card>
              </TabsContent>
            </Tabs>

            {!medalsData || medalsData.team_ranking?.length === 0 ? (
              <Card className="bg-[#141414] border-[#27272A]">
                <CardContent>
              <EstadoVazio icone={Award} titulo="Nenhuma medalha ainda" descricao="As medalhas serão exibidas após a finalização do campeonato" />
            </CardContent>
              </Card>
            ) : (
              <>
                {/* Team Ranking - Olympic Style */}
                <Card className="bg-[#141414] border-[#27272A]">
                  <CardHeader>
                    <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-[#D4AF37]" />
                      Ranking por Equipe
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="tabela w-full">
                        <thead>
                          <tr className="border-b border-[#27272A]">
                            <th className="text-left py-3 px-4 text-[#71717A]">#</th>
                            <th className="text-left py-3 px-4 text-[#71717A]">Equipe</th>
                            <th className="text-center py-3 px-4">
                              <Medal className="w-5 h-5 medalha-ouro mx-auto" />
                            </th>
                            <th className="text-center py-3 px-4">
                              <Medal className="w-5 h-5 medalha-prata mx-auto" />
                            </th>
                            <th className="text-center py-3 px-4">
                              <Medal className="w-5 h-5 medalha-bronze mx-auto" />
                            </th>
                            <th className="text-center py-3 px-4 text-[#71717A]">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {medalsData.team_ranking.map((team, index) => (
                            <tr 
                              key={team.team_id} 
                              className={`border-b border-[#27272A]/50 ${index < 3 ? 'bg-[#1A1A1A]' : ''}`}
                            >
                              <td className="py-3 px-4">
                                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                  index === 0 ? 'bg-[#D4AF37] text-black' :
                                  index === 1 ? 'bg-gray-400 text-black' :
                                  index === 2 ? 'bg-amber-700 text-white' :
                                  'text-[#71717A]'
                                }`}>
                                  {index + 1}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-white font-medium">{team.team_name}</td>
                              <td className="py-3 px-4 text-center text-white font-bold">{team.gold}</td>
                              <td className="py-3 px-4 text-center text-white font-bold">{team.silver}</td>
                              <td className="py-3 px-4 text-center text-white font-bold">{team.bronze}</td>
                              <td className="py-3 px-4 text-center text-[#D4AF37] font-bold">
                                {team.gold + team.silver + team.bronze}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Individual Medals */}
                <Card className="bg-[#141414] border-[#27272A]">
                  <CardHeader>
                    <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#D4AF37]" />
                      Medalhas Individuais ({medalsData.medals?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {medalsData.medals?.map((medal) => (
                        <div 
                          key={medal._id}
                          className={`p-4 rounded-lg border ${
                            medal.type === 'gold' ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' :
                            medal.type === 'silver' ? 'bg-[#C0C0C0]/10 border-[#C0C0C0]/30' :
                            'bg-[#CD7F32]/10 border-[#CD7F32]/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Medal className={`w-8 h-8 ${
                              medal.type === 'gold' ? 'medalha-ouro' :
                              medal.type === 'silver' ? 'medalha-prata' :
                              'medalha-bronze'
                            }`} />
                            <div>
                              <p className="font-medium text-white">{medal.category}</p>
                              <p className="text-xs text-[#71717A] capitalize">
                                {medal.type === 'gold' ? 'Ouro' : medal.type === 'silver' ? 'Prata' : 'Bronze'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </PageContent>
  );
}
